'use client';

import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function HubMeetupRsvpPanel({
  meetup,
  currentUser,
  loginRedirectPath = '',
  onStatusChange,
  messages = {},
}) {
  const [rsvpStatus, setRsvpStatus] = useState(
    currentUser ? 'loading' : 'none',
  );
  const [joinInfo, setJoinInfo] = useState(null);
  const [joinInfoLoading, setJoinInfoLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isHost = Boolean(
    currentUser?.userNumber &&
    meetup?.hostUserNumber &&
    currentUser.userNumber === meetup.hostUserNumber,
  );
  const isAdmin = ['admin', 'superadmin'].includes(currentUser?.role || '');
  const isModerator = isHost || isAdmin;
  const msg = (path, fallback = '') => readHubMessage(messages, path, fallback);
  const loginHref = `/login?redirect=${encodeURIComponent(
    loginRedirectPath || `/hub/meetups/${meetup.meetupNumber}`,
  )}`;

  useEffect(() => {
    if (typeof onStatusChange === 'function') {
      onStatusChange(rsvpStatus);
    }
  }, [onStatusChange, rsvpStatus]);

  useEffect(() => {
    let isMounted = true;

    const loadRsvpState = async () => {
      if (!currentUser || isModerator) {
        setRsvpStatus('none');
        return;
      }

      try {
        const response = await clientFetch(
          `/api/hub/meetups/${meetup.meetupNumber}/rsvp`,
        );

        if (!isMounted) {
          return;
        }

        setRsvpStatus(response?.status || 'none');
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRsvpStatus('none');
        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'meetup.rsvpPanel.errors.loadStatus',
              'Unable to load RSVP status.',
            ),
        );
      }
    };

    loadRsvpState();

    return () => {
      isMounted = false;
    };
  }, [currentUser, isModerator, meetup.meetupNumber]);

  useEffect(() => {
    let isMounted = true;

    const loadJoinInfo = async () => {
      if (!currentUser || rsvpStatus !== 'approved') {
        setJoinInfo(null);
        return;
      }

      setJoinInfoLoading(true);

      try {
        const response = await clientFetch(
          `/api/hub/meetups/${meetup.meetupNumber}/join-info`,
        );

        if (!isMounted) {
          return;
        }

        setJoinInfo(response?.joinInfo || null);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'meetup.rsvpPanel.errors.loadJoinInfo',
              'Unable to load meetup details.',
            ),
        );
      } finally {
        if (isMounted) {
          setJoinInfoLoading(false);
        }
      }
    };

    loadJoinInfo();

    return () => {
      isMounted = false;
    };
  }, [currentUser, rsvpStatus, meetup.meetupNumber]);

  const requestRsvp = async () => {
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await clientFetch(
        `/api/hub/meetups/${meetup.meetupNumber}/rsvp`,
        {
          method: 'POST',
        },
      );

      setRsvpStatus(response?.status || 'pending');
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'meetup.rsvpPanel.errors.request',
            'Unable to RSVP for this meetup.',
          ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRsvp = async () => {
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await clientFetch(`/api/hub/meetups/${meetup.meetupNumber}/rsvp`, {
        method: 'DELETE',
      });

      setRsvpStatus('none');
      setJoinInfo(null);
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg('meetup.rsvpPanel.errors.cancel', 'Unable to cancel RSVP.'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="app-panel hub-action-panel hub-action-panel--meetups">
      <div className="hub-action-panel__header">
        <div>
          <p className="hub-section__eyebrow hub-section__eyebrow--warning">
            {msg('meetup.rsvpPanel.eyebrow', 'Meetup RSVP')}
          </p>
          <h2>{msg('meetup.rsvpPanel.title', 'Reserve your spot')}</h2>
        </div>
        {rsvpStatus === 'approved' ? (
          <span className="hub-pill hub-pill--accent">
            {msg('meetup.rsvpPanel.approvedBadge', 'Approved')}
          </span>
        ) : rsvpStatus === 'pending' ? (
          <span className="hub-pill hub-pill--warning">
            {msg('meetup.rsvpPanel.pendingBadge', 'Pending')}
          </span>
        ) : null}
      </div>

      {!currentUser ? (
        <div className="hub-inline-stack">
          <p className="shop-panel__meta">
            {msg(
              'meetup.rsvpPanel.loginHint',
              'Sign in to send an RSVP request for this meetup.',
            )}
          </p>
          <div className="account-form__actions">
            <Link href={loginHref} className="app-button app-button--primary">
              {msg('meetup.rsvpPanel.loginCta', 'Log in to RSVP')}
            </Link>
          </div>
        </div>
      ) : isModerator ? (
        <p className="shop-panel__meta">
          {msg(
            'meetup.rsvpPanel.moderatorHint',
            'You are managing this meetup. Review attendee requests below.',
          )}
        </p>
      ) : rsvpStatus === 'loading' ? (
        <p className="shop-panel__meta">
          {msg('meetup.checkingRsvp', 'Checking your request status…')}
        </p>
      ) : (
        <div className="hub-inline-stack">
          {rsvpStatus === 'approved' ? (
            <div className="hub-join-info">
              <p className="shop-panel__meta">
                {msg(
                  'meetup.rsvpPanel.approvedHint',
                  'Your RSVP is approved. Meetup access details are available below.',
                )}
              </p>
              {joinInfoLoading ? (
                <p className="shop-panel__meta">
                  {msg(
                    'meetup.rsvpPanel.loadingJoinInfo',
                    'Loading meetup details...',
                  )}
                </p>
              ) : null}
              {joinInfo?.address ? (
                <p>
                  <strong>{msg('meetup.joinAddressLabel', 'Address')}:</strong>{' '}
                  {joinInfo.address}
                </p>
              ) : null}
              {joinInfo?.onlineUrl ? (
                <p>
                  <strong>{msg('meetup.joinOnlineLabel', 'Link')}:</strong>{' '}
                  <a href={joinInfo.onlineUrl} target="_blank" rel="noreferrer">
                    {joinInfo.onlineUrl}
                  </a>
                </p>
              ) : null}
              {joinInfo?.instructions ? (
                <p className="hub-detail__description">
                  {joinInfo.instructions}
                </p>
              ) : !joinInfoLoading ? (
                <p className="shop-panel__meta">
                  {msg(
                    'meetup.joinEmpty',
                    'Join details will appear here once set.',
                  )}
                </p>
              ) : null}
            </div>
          ) : rsvpStatus === 'pending' ? (
            <p className="shop-panel__meta">
              {msg(
                'meetup.rsvpPanel.pendingHint',
                'Your RSVP request is pending host approval. You can withdraw it below.',
              )}
            </p>
          ) : rsvpStatus === 'rejected' ? (
            <p className="shop-panel__meta">
              {msg(
                'meetup.rsvpPanel.rejectedHint',
                'Your last RSVP request was rejected. You can send a fresh request if the meetup is still open.',
              )}
            </p>
          ) : (
            <p className="shop-panel__meta">
              {msg(
                'meetup.rsvpPanel.noneHint',
                'Send an RSVP request here. Approved requests unlock the meetup join information below.',
              )}
            </p>
          )}

          {errorMessage ? (
            <div className="auth-message auth-message--error">
              {errorMessage}
            </div>
          ) : null}

          <div className="account-form__actions">
            {rsvpStatus === 'approved' || rsvpStatus === 'pending' ? (
              <button
                type="button"
                className="app-button app-button--ghost"
                onClick={cancelRsvp}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? msg('meetup.rsvpPanel.updating', 'Updating...')
                  : msg('meetup.cancelRsvp', 'Leave meetup')}
              </button>
            ) : (
              <button
                type="button"
                className="app-button app-button--primary"
                onClick={requestRsvp}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? msg('meetup.rsvpPanel.sending', 'Sending...')
                  : msg('meetup.rsvp', 'Request to Join')}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
